import {
  IconButton,
  SearchInput,
  Tooltip,
} from '@alefeducation/ui-elements-library-core';
import {
  AngleRight,
  Download,
  RedoAlt,
} from '@alefeducation/ui-elements-library-icons';
import MultiSelect2 from '@alefeducation/ui-elements-library-core/lib/MultiSelect2';
import { MultiSelectOptionItem } from '@alefeducation/ui-elements-library-core/lib/components/MultiSelect2/MultiSelect2';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import { HorizontalBreaker, InfoTuple, StatusInfo } from './InfoTuple';
import { EsisLogsMain } from './EsisLogs';
import {
  useEsisJobChangeType,
  useEsisJobDetail,
  useEsisJobReport,
} from './useEsisJobs';
import {
  getDefaultDateFormat,
  getLastModifiedTime,
  jobDetailColumns,
} from '../../utils/esisLogsUtils';
import DataGrid from '../../components/DataGrid/DataGrid';
import { ESIS_JOBS } from '../../components/Routing/routeHelpers';
import Loader from '../../components/Loader/Loader';
import downloadFile from '../../utils/fileDownload';

const styles = {
  breadCrumbLink: {
    textDecoration: 'none',
    cursor: 'pointer',
    color: '#404040',
    fontSize: 16,
    fontWeight: 300,
  },
};

const NOT_APPLICABLE = 'N/A';

export const getJobCompletedInTime = (jobInfo: any) => {
  if (
    jobInfo.startTime === NOT_APPLICABLE ||
    jobInfo.endTime === NOT_APPLICABLE
  ) {
    return '';
  }
  const startTime = moment(jobInfo.startTime);
  const endTime = moment(jobInfo.endTime);
  const duration = endTime.diff(startTime, 'millisecond');
  const momentTime = moment.utc(duration);

  const hours = momentTime.hours();
  const minutes = momentTime.minutes();
  const seconds = momentTime.seconds();

  let timeString = `${seconds}sec`;

  if (minutes) timeString = `${minutes}min ${timeString}`;

  if (hours) timeString = `${hours}hr ${timeString}`;

  return timeString;
};

const EsisJobDetail = () => {
  const [search, setSearch] = useState<string>('');
  const [changeType, setChangeType] = useState<MultiSelectOptionItem[]>([]);
  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(20);
  const { esisJobId } = useParams<{ esisJobId: string }>();

  const { esisJobData, fetchJobDetail, isFetchLoaading } = useEsisJobDetail();
  const { esisJobChangeTypeData, fetchJobChangeType } = useEsisJobChangeType();
  const {
    esisJobReportData,
    fetchJobReport,
    isFetchLoaading: reportDataLoading,
  } = useEsisJobReport();

  useEffect(() => {
    fetchJobDetail({ variables: { jobId: esisJobId } });
    fetchJobChangeType({
      variables: { jobId: esisJobId },
    });
    fetchJobReport({
      variables: {
        jobId: esisJobId,
        page: 1,
        size: 20,
        searchTerm: '',
        changeType: '',
      },
    });
  }, []);

  useEffect(() => {
    fetchJobReport({
      variables: {
        jobId: esisJobId,
        searchTerm: search,
        page: page - 1,
        size,
        changeType:
          changeType.length > 0 ? changeType.map((c) => c.id).join(', ') : '',
      },
    });
  }, [esisJobId, search, page, size, changeType]);

  const getJobTypes = () => {
    return (
      esisJobChangeTypeData?.map((item) => ({ id: item, type: item })) || []
    );
  };

  const onSearchChange = (value: string) => {
    setSearch(value);
  };

  const onSearch = (searchTerm: string) => {
    setPage(1);
    setSize(20);
  };

  const onRecordFilterChange = (type: MultiSelectOptionItem[]) => {
    setChangeType(type);
    setSearch('');
  };

  const refetchData = () => {
    fetchJobReport({
      variables: {
        jobId: esisJobId,
        searchTerm: search,
        page: page - 1,
        size,
        changeType:
          changeType.length > 0 ? changeType.map((c) => c.id).join(', ') : '',
      },
    });
  };

  const downloadReport = async () => {
    const url = `/organization-service/api/job/${esisJobId}/reports`;

    try {
      await downloadFile(url, `${esisJobId}.csv`);
    } catch (ex) {
      console.log('File download failed');
    }
  };

  const jobCompletedInTime =
    esisJobData && !isFetchLoaading ? getJobCompletedInTime(esisJobData) : '';
  return (
    <EsisLogsMain>
      <StyledContainer>
        <BreadCrumb>
          <Link
            to={ESIS_JOBS.esisLogs}
            style={styles.breadCrumbLink}
            key={`breadcrum-item`}
          >
            <span className="alef-breadcrumbs__label alef-breadcrumbs__label-inactive">
              Esis Logs
            </span>
          </Link>
          <AngleRight />
          <Crumb> Records </Crumb>
        </BreadCrumb>
      </StyledContainer>
      <StyledTable>
        <JobDetails>
          {isFetchLoaading ? (
            <Loader />
          ) : (
            <JobDetailsInfo>
              <InfoTuple
                label="File Name"
                value={esisJobData?.fileName ?? ''}
              />
              <InfoTuple label="Started By" value={esisJobData?.startedBy} />
              <InfoTuple
                label="Last modified"
                value={getDefaultDateFormat(
                  getLastModifiedTime(esisJobData?.createdOn ?? ''),
                )}
              />
              <InfoTuple
                label="Status"
                value={
                  jobCompletedInTime ? (
                    <Tooltip
                      content={`Took ${jobCompletedInTime}`}
                      data-testid={'jobs-info-tooltip'}
                      id={`${esisJobData?.id ?? ''}-jobs-info-tooltip`}
                      position={'bottom'}
                    >
                      <StatusInfo status={esisJobData?.status ?? ''} />
                    </Tooltip>
                  ) : (
                    <StatusInfo status={esisJobData?.status ?? ''} />
                  )
                }
              />
              <HorizontalBreaker />
              <InfoTuple label="Job Reference" value={esisJobData?.id ?? ''} />
            </JobDetailsInfo>
          )}
        </JobDetails>
        <JobContents>
          <TopHeader>
            <Filters>
              <Select>
                <MultiSelect2
                  items={getJobTypes()}
                  selectedItems={changeType}
                  textKey="type"
                  data-testid="job-type"
                  selectionChanged={onRecordFilterChange}
                />
              </Select>
              <IconButton
                size="large"
                onClick={refetchData}
                tooltipPosition="bottom"
                tooltipContent={'Refresh'}
              >
                <RedoAlt color="#485465" />
              </IconButton>
              <IconButton
                className="download-sis-report"
                size="large"
                onClick={downloadReport}
                tooltipPosition="bottom"
                tooltipContent={'Download Report'}
              >
                <Download color="#485465" />
              </IconButton>
            </Filters>
            <Search>
              <SearchInput
                width="400px"
                value={search}
                placeholder={'Search...'}
                onChange={(e) => {
                  onSearchChange(e.target.value);
                }}
                onClear={() => {
                  setSearch('');
                }}
                onSearch={onSearch}
              />
            </Search>
          </TopHeader>
          <Table>
            <DataGrid
              isDataLoading={reportDataLoading}
              allowPagination
              data={esisJobReportData?.items || []}
              columns={jobDetailColumns}
              page={page}
              size={size}
              pageCount={esisJobReportData?.totalCount ?? 0}
              onPageChange={(page) => setPage(page)}
              onPageSizeChange={(size) => setSize(size)}
            />
          </Table>
        </JobContents>
      </StyledTable>
    </EsisLogsMain>
  );
};

export default EsisJobDetail;

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  height: 40px;
`;
const Select = styled.div`
  width: 287px;
`;

const Filters = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  aligh-items: center;
`;

const Search = styled.div``;

const TopHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
`;

const Table = styled.div`
  margin-top: 10px;
  margin-bottom: 20px;
`;

const JobContents = styled.div`
  margin-top: 16px;
  border-radius: 10px;
  border: 1px solid #dadde0;
`;

export const StyledTable = styled.div`
  background: ${({ theme }: any) => theme.colors.primary[0]};
  border-radius: 10px;
  padding: 16px 24px;

  .loading,
  .empty-message-view {
    height: 80%;
  }
`;

const BreadCrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #404040;
`;

const Crumb = styled.span`
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  text-align: left;
`;

const JobDetails = styled.div`
  border: 1px solid #dadde0;
  border-radius: 8px;
  background: #fff;
  overflow-y: scroll;
  height: 100%;
  padding: 16px 24px;
`;

const JobDetailsInfo = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
`;
